import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import WideContent from 'core/layout/content/wide-content';
import Translate from 'ui/atoms/translate';
import WithAuthenticatedPage from 'core/auth/components/with-authenticated-page';
import { AppType } from 'core/auth/types';
import useApiCall from 'hooks/use-api-call';
import { AdminApi } from 'api/apis';
import LoadingRing from 'ui/atoms/loading-ring';
import { AdminOrganizationDetails, LoginTypeEnum } from 'api/models';
import BoxedContent from 'ui/molecules/boxed-content';
import Section from 'ui/atoms/section';
import Button from 'ui/atoms/button';
import ActionButtons from 'src/ui/molecules/action-buttons';
import CardItem from 'src/ui/atoms/card-item';
import CopyButton from 'src/ui/molecules/copy-button';
import EditOrganization from '../organizations/edit-organization';
import OrganizationUsersTable from './organization-users-table';
import OrganizationGroupsTable from './organization-groups-table';
import Link from 'core/routing/link';
import PlaceholderFallback from 'src/ui/atoms/placeholder-fallback';
import useTranslate from 'src/ui/hooks/use-translate';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import ViewPermissionMissing from 'src/core/components/view-permission-missing';
import { useDispatch } from 'store/hooks';
import { push } from 'connected-react-router';
import makeLink from 'src/helper/make-link';
import { ISSUER_ROUTES } from '../routes.config';

interface IssuerOrganizationDetailsPageProps {
  organizationId: string;
}

const IssuerOrganizationDetailsPage: FunctionComponent<IssuerOrganizationDetailsPageProps> = ({ organizationId }) => {
  const [organization, setOrganization] = useState<AdminOrganizationDetails>();
  const [isEditOrganizationtModalOpen, setEditOrganizationModalOpen] = useState(false);
  const [hasPermissionError, setHasPermissionError] = useState(false);

  const { withApi, makeAuthenticatedApi } = useApiCall();
  const translate = useTranslate();
  const dispatch = useDispatch();

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const serviceContractLabels = useMemo(() => {
    if (!organization || organization?.serviceContracts.length === 0) return;

    return organization.serviceContracts
      ?.map((contract) =>
        translate(`studioOrganizationsTable.serviceContracts.${snakeToCamelCaseWithNumbers(contract)}`),
      )
      .join(', ');
  }, [organization?.serviceContracts, translate]);

  const fetchOrganizationDetails = useCallback(() => {
    setHasPermissionError(false);

    withApi(async () => {
      try {
        const organizationDetails = await adminApi.adminOrganizationsRetrieve({ id: organizationId });
        setOrganization(organizationDetails);
      } catch (e: any) {
        if (e.status === 403) {
          setHasPermissionError(true);
        }
      }
    });
  }, [organizationId, adminApi]);

  useEffect(() => {
    fetchOrganizationDetails();
  }, [withApi, adminApi, organizationId]);

  const goTo = (userId: string) => {
    dispatch(push(makeLink(ISSUER_ROUTES.userDetails, { userId })));
  };

  if (hasPermissionError) {
    return <ViewPermissionMissing />;
  }

  if (!organization) return <LoadingRing />;

  return (
    <>
      <Section>
        <WideContent
          title={<Translate name="issuerOrganizationsDetails.title" args={[organization && organization.name]} />}
          titleSpacing="larger"
        >
          <BoxedContent releaseSpaceWhenTitleIsEmpty={true} title={<Translate name="common.generalInformation" />}>
            <CardItem title={<Translate name="studioOrganizationsTable.organization" />} content={organization.name} />
            <CardItem
              title={<Translate name="issuerOrganizationsDetails.organisationId" />}
              content={organizationId}
              icon={<CopyButton valueToCopy={organizationId} />}
            />
            <CardItem
              title={<Translate name="studioOrganizationsTable.loginMethod" />}
              content={
                organization.loginType === LoginTypeEnum.GOOGLE_SSO ? (
                  <Translate name="studioOrganizationsTable.googleSsoLogin" />
                ) : (
                  <Translate name="studioOrganizationsTable.passwordLogin" />
                )
              }
            />
            <CardItem
              title={<Translate name="studioOrganizationsTable.serviceContracts.title" />}
              content={<PlaceholderFallback>{serviceContractLabels}</PlaceholderFallback>}
            />
            <ActionButtons spacingTop="regular">
              <Button variant="secondary" onClick={() => setEditOrganizationModalOpen(true)}>
                <Translate name="common.edit" />
              </Button>
            </ActionButtons>
          </BoxedContent>
        </WideContent>
        {isEditOrganizationtModalOpen && (
          <EditOrganization
            onClose={() => setEditOrganizationModalOpen(false)}
            onEditOrganization={fetchOrganizationDetails}
            data={organization}
          />
        )}
      </Section>
      <OrganizationUsersTable organization={organization} goToDetails={(userId: string) => goTo(userId)} />
      <OrganizationGroupsTable organization={organization} goToDetails={(groupId: string) => goTo(groupId)} />
      <WideContent>
        <ActionButtons align="start">
          <Link icon="arrow-left" to="/dashboard/organizations">
            <Translate as="span" name="dashboard.issuerOrganizations" />
          </Link>
        </ActionButtons>
      </WideContent>
    </>
  );
};

export default WithAuthenticatedPage(IssuerOrganizationDetailsPage, AppType.ISSUER);
